import React, { useRef, useState, useEffect } from "react";
import "./SportsArena.css";

import { Swiper, SwiperSlide } from "swiper/react";
import { EffectFade, Pagination, Navigation, Autoplay } from "swiper/modules";

import "swiper/css";
import "swiper/css/effect-fade";
import "swiper/css/pagination";
import "swiper/css/navigation";

// Images
import swimmingBg from "../assets/swimming-bg.b32ffcbdb26eaea55a34_compressed.webp";
import swimmingCard from "../assets/home-pool.jfif";
import basketballBg from "../assets/basketball-bg.551ea25aefcc6acb1cba_compressed.webp";
import basketballCard from "../assets/home-basketball.jfif";
import badmintonBg from "../assets/indoor_badminton_bg.a1004fdd6ba8cb632716_compressed.webp";
import badmintonCard from "../assets/badmintion-image.jpeg";
import pickleballBg from "../assets/indoor-pickleball-bg.db8c9c9eaa9a797db223_compressed.webp";
import pickleballCard from "../assets/pickle-ball.jpeg";
import cricketBg from "../assets/cricket-1.jpeg";
import cricketCard from "../assets/cricket-ball-machine.jpeg";
import indoorSportsBg from "../assets/indoor-sports-bg.7853c055bf5b1241012e_compressed.webp";
import indoorSportsCard from "../assets/home-chess.jfif";

const sportsFacilities = [
  {
    title: "Swimming Pool",
    bgImg: swimmingBg,
    cardImg: swimmingCard,
    desc: `At Vivek Vardhini Creative Minds School, our temperature-controlled swimming pool offers students a safe, hygienic, and comfortable environment to learn and enjoy the art of swimming throughout the year. Guided by a certified professional coach, students are trained in proper techniques, water safety, and fitness routines suitable for their age and skill level. The pool’s controlled temperature ensures an optimal experience in every season, making swimming a refreshing blend of sport, confidence-building, and physical development. With structured sessions, vigilant supervision, and world-class facilities, we ensure that every child learns with joy, discipline, and complete safety.`,
  },

  {
    title: "Basketball",
    bgImg: basketballBg,
    cardImg: basketballCard,
    desc: `Our school features a well-designed, full-sized basketball court that provides students with the perfect space to learn, practise, and excel in the sport. Under the guidance of a professional, certified basketball coach, students receive structured training that builds stamina, discipline, teamwork, and technical skills. The court is maintained to high safety standards, ensuring a secure environment for both beginners and advanced players. Through regular practice sessions and skill-based drills, we nurture young athletes and encourage them to participate confidently in inter-school and state-level competitions.`,
  },

  {
    title: "Indoor Badminton Court",
    bgImg: badmintonBg,
    cardImg: badmintonCard,
    desc: `Our school boasts a state-of-the-art indoor badminton court designed to provide students with a safe, spacious, and all-weather sporting environment. Equipped with high-quality flooring, proper lighting, and well-marked courts, it ensures an excellent playing experience for beginners as well as advanced learners. Under the guidance of our professional badminton coach, students receive structured training that focuses on physical fitness, correct techniques, agility, and sportsmanship. Regular practice sessions and skill-building drills help young learners develop confidence and discipline while nurturing their talent in the sport. This modern facility reflects our commitment to encouraging an active lifestyle and promoting excellence in sports as an integral part of holistic education.`,
  },

  {
    title: "Indoor Pickleball Court",
    bgImg: pickleballBg,
    cardImg: pickleballCard,
    desc: `Our school features a modern Indoor Pickleball Court, offering students a safe, all-weather facility to explore one of today’s fastest-growing sports. The court is built with premium flooring, clear court markings, and bright indoor lighting to ensure a smooth and engaging playing experience throughout the year. Training is conducted by our professional pickleball coach, who guides students through skill-building drills, movement techniques, and age-appropriate game strategies. The programme focuses on developing agility, focus, teamwork, and confidence while keeping the sessions enjoyable and interactive. This facility reflects our commitment to providing diverse, contemporary sports opportunities that support students’ physical development and overall well-being.`,
  },

  {
    title: "Cricket",
    bgImg: cricketBg,
    cardImg: cricketCard,
    desc: `We believe sports play a vital role in shaping confidence and character. Our cricket programme is led by a professional coach who trains students with a perfect blend of technique, discipline, and modern practice methods. From mastering basic skills to understanding advanced gameplay strategies, students learn through structured sessions, fitness routines, and practice matches. Our aim is to nurture young talent, promote teamwork, and provide every child the opportunity to grow as a skilled and responsible sportsperson.`,
  },

  {
    title: "Indoor Sports",
    bgImg: indoorSportsBg,
    cardImg: indoorSportsCard,
    desc: `At Vivek Vardhani Creative Minds School, our Indoor Sports Arena offers students a vibrant and well-equipped space to practice Table Tennis, Chess and Carrom under the guidance of professional coaches. Designed to sharpen both physical agility and mental strength, each sport is taught with structured sessions that build technique, confidence and sportsmanship. Students benefit from expert coaching, safe playing conditions, and regular practice opportunities that help them develop focus, discipline and competitive spirit. Through these indoor games, we create a balanced environment where students learn, play and grow with enthusiasm and excellence.`,
  },
];


// Typewriter effect
const TypewriterText = ({ text, speed = 20 }) => {
  const [displayed, setDisplayed] = useState("");
  useEffect(() => {
    let i = 0;
    const interval = setInterval(() => {
      setDisplayed(text.slice(0, i));
      i++;
      if (i > text.length) clearInterval(interval);
    }, speed);
    return () => clearInterval(interval);
  }, [text, speed]);
  return <p>{displayed}</p>;
};

const SportsArena = () => {
  const tiltRef = useRef(null);

  const handleMouseMove = (e) => {
    const el = tiltRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    el.style.setProperty("--tiltX", `${y * -6}deg`);
    el.style.setProperty("--tiltY", `${x * 8}deg`);
  };

  const resetTilt = () => {
    const el = tiltRef.current;
    if (!el) return;
    el.style.setProperty("--tiltX", "0deg");
    el.style.setProperty("--tiltY", "0deg");
  };

  return (
    <section className="infra-hero">
      {/* TITLE */}
      <h2 className="infra-title">Sports Arena</h2>

      {/* DESKTOP SWIPER */}
      <div className="infra-desktop-view">
        <div className="infra-container">
          <Swiper
            modules={[EffectFade, Pagination, Navigation, Autoplay]}
            effect="fade"
            fadeEffect={{ crossFade: true }}
            loop={true}
            autoplay={{ delay: 6000, disableOnInteraction: false }}
            speed={1200}
            pagination={{ clickable: true }}
            navigation
            className="infra-hero-swiper"
          >
            {sportsFacilities.map((item) => (
              <SwiperSlide key={item.title}>
                <div className="infra-clip">
                  <div
                    className="infra-tilt"
                    ref={tiltRef}
                    onMouseMove={handleMouseMove}
                    onMouseLeave={resetTilt}
                  >
                    <div className="infra-bg">
                      <img src={item.bgImg} alt="" />
                    </div>
                    <div className="infra-overlay" />
                    <div className="infra-gate">
                      <img src={item.cardImg} alt={item.title} />
                      <div className="infra-hover-text">
                        <h3>{item.title}</h3>
                        <p>{item.desc}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </div>

      {/* MOBILE / TABLET */}
      <div className="infra-mobile-view">
        {sportsFacilities.map((item) => (
          <div key={item.title} className="infra-mobile-card">
            <h3 className="infra-mobile-title">{item.title}</h3>
            <img src={item.cardImg} alt={item.title} className="infra-mobile-img" />
            <TypewriterText text={item.desc} speed={20} />
          </div>
        ))}
      </div>
    </section>
  );
};

export default SportsArena;
